import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseError } from '@utils/types/shared/base';
import { OnPremOrganizationsResponse } from '@utils/types/back-office/on-prem-billing';

/**
 * Hook to fetch organizations with billing_channel=ON_PREM
 *
 * GET /api/admin/billing/on-prem/organizations
 * Query params:
 *   - organization_id: filter by exact org ID
 *   - q: search by organization name (partial match)
 *   - email: search by email (partial match)
 *   - plan_sku: filter by plan SKU (partial match)
 *   - sort: organization_id | organization_name | email | plan_sku
 *   - direction: asc | desc
 *   - limit, offset
 */
export const useGetOnPremOrganizations = (options?: {
  offset?: number;
  limit?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  search?: string;
  email?: string;
  organizationId?: number;
  planSku?: string;
}) => {
  const {
    offset = 0,
    limit = 25,
    sort,
    direction,
    search,
    email,
    organizationId,
    planSku
  } = options || {};

  const fetchOrganizations = async () => {
    const params = new URLSearchParams();
    params.append('offset', String(offset));
    params.append('limit', String(limit));
    if (sort) params.append('sort', sort);
    if (direction) params.append('direction', direction);
    if (search) params.append('q', search);
    if (email) params.append('email', email);
    if (organizationId != null)
      params.append('organization_id', String(organizationId));
    if (planSku) params.append('plan_sku', planSku);

    const url = `${URL_EXTENSION}/admin/billing/on-prem/organizations?${params.toString()}`;
    const response = await Axios.get<OnPremOrganizationsResponse>(url);
    return response.data;
  };

  return useQuery<OnPremOrganizationsResponse, AxiosError<BaseError>>(
    [
      'onPremOrganizations',
      offset,
      limit,
      sort,
      direction,
      search,
      email,
      organizationId,
      planSku
    ],
    fetchOrganizations,
    {
      useErrorBoundary: false,
      keepPreviousData: true
    }
  );
};
